import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  User, 
  Building2, 
  ChevronRight, 
  ArrowLeft, 
  Download, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  ShieldAlert,
  Mail,
  Loader2,
  X,
  Home
} from 'lucide-react';
import LiquidEther from '../components/acceuil/LiquidEther'; 

export default function AuditPage() {
  const [step, setStep] = useState('choice'); // 'choice' | 'questions' | 'results'
  const [auditType, setAuditType] = useState(null); // 'particulier' | 'entreprise'
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // --- BASE DE DONNÉES DES QUESTIONS ---
  const questionsData = {
    particulier: [
      { q: "Utilisez-vous la double authentification sur vos comptes importants ?", weight: 3, inverse: false, rec: "Activez immédiatement la double authentification (MFA) sur vos boîtes mail et réseaux sociaux." },
      { q: "Utilisez-vous le même mot de passe sur plusieurs sites ?", weight: 3, inverse: true, rec: "Installez un gestionnaire de mots de passe pour générer des clés uniques." },
      { q: "Avez-vous un gestionnaire de mots de passe ?", weight: 2, inverse: false, rec: "Centralisez vos accès sur un coffre-fort chiffré (Bitwarden, Dashlane)." },
      { q: "Votre adresse mail a-t-elle déjà été compromise dans une fuite de données ?", weight: 2, inverse: true, rec: "Surveillez votre exposition sur des plateformes d'alerte et changez vos accès." },
      { q: "Votre profil Facebook, Instagram, LinkedIn ou TikTok est-il public ?", weight: 2, inverse: true, rec: "Limitez la visibilité publique de vos profils sociaux pour réduire la collecte d'informations." },
      { q: "Partagez-vous votre numéro de téléphone ou votre adresse en ligne ?", weight: 2, inverse: true, rec: "Réduisez au strict minimum les données personnelles publiées." },
      { q: "Acceptez-vous facilement les demandes d’amis ou messages d’inconnus ?", weight: 2, inverse: true, rec: "Filtrez les contacts inconnus et vérifiez l'identité des personnes." },
      { q: "Avez-vous déjà reçu des mails ou SMS suspects ?", weight: 1, inverse: true, rec: "Mettez en place une vigilance renforcée sur les messages inattendus." },
      { q: "Vérifiez-vous les liens avant de cliquer ?", weight: 2, inverse: false, rec: "Contrôlez systématiquement l'URL réelle avant de cliquer." },
      { q: "Votre ordinateur et votre téléphone sont-ils régulièrement mis à jour ?", weight: 2, inverse: false, rec: "Activez les mises à jour automatiques pour corriger les failles actives." },
      { q: "Utilisez-vous un antivirus ou une protection de sécurité ?", weight: 2, inverse: false, rec: "Installez une protection de sécurité fiable et maintenez-la active." },
      { q: "Vos comptes principaux ont-ils des mots de passe différents ?", weight: 3, inverse: false, rec: "Utilisez des mots de passe uniques pour chaque compte critique." },
      { q: "Sauvegardez-vous vos données importantes ?", weight: 2, inverse: false, rec: "Mettez en place des sauvegardes régulières de vos données sensibles." },
      { q: "Utilisez-vous le Wi-Fi public sans VPN ?", weight: 2, inverse: true, rec: "Évitez les connexions aux réseaux ouverts sans chiffrement via un VPN." },
      { q: "Savez-vous reconnaître une tentative de phishing ?", weight: 1, inverse: false, rec: "Formez-vous aux signaux faibles du phishing (domaines de l'expéditeur, URL)." }
    ],
    entreprise: [
      { q: "Votre entreprise utilise-t-elle la double authentification ?", weight: 3, inverse: false, rec: "Déployez de manière obligatoire la double authentification (MFA) sur l'ensemble du parc d'identités." },
      { q: "Les collaborateurs ont-ils des mots de passe uniques et sécurisés ?", weight: 2, inverse: false, rec: "Mettez en place une politique de mots de passe stricte couplée à un gestionnaire d'identités." },
      { q: "Utilisez-vous un gestionnaire de mots de passe professionnel ?", weight: 2, inverse: false, rec: "Centralisez les secrets de l'entreprise dans un gestionnaire professionnel." },
      { q: "Avez-vous déjà réalisé un audit de cybersécurité ?", weight: 2, inverse: false, rec: "Programmez un audit de cybersécurité régulier pour évaluer votre niveau de maturité." },
      { q: "Avez-vous déjà réalisé un test d’intrusion ?", weight: 3, inverse: false, rec: "Planifiez un test d'intrusion (Pentest) annuel pour identifier vos vulnérabilités." },
      { q: "Vos employés sont-ils sensibilisés au phishing ?", weight: 2, inverse: false, rec: "Lancez des campagnes de simulation de phishing trimestrielles." },
      { q: "Disposez-vous d’une politique de sécurité informatique ?", weight: 2, inverse: false, rec: "Formalisez une politique de sécurité informatique claire, diffusée et appliquée." },
      { q: "Vos postes et serveurs sont-ils régulièrement mis à jour ?", weight: 2, inverse: false, rec: "Industrialisez la gestion des correctifs sur les postes et serveurs." },
      { q: "Vos sauvegardes sont-elles régulières et testées ?", weight: 3, inverse: false, rec: "Sanctuarisez vos sauvegardes via une stratégie 3-2-1 immuable contre les Ransomwares." },
      { q: "Avez-vous un plan de réponse en cas d’incident ?", weight: 2, inverse: false, rec: "Rédigez et testez un plan de continuité d'activité (PCA/PRA)." },
      { q: "Vos services exposés sur Internet sont-ils surveillés ?", weight: 2, inverse: false, rec: "Mettez en place une surveillance continue de vos actifs exposés." },
      { q: "Votre site web a-t-il déjà été testé contre les failles courantes ?", weight: 2, inverse: false, rec: "Testez régulièrement vos applications web avant toute mise en production majeure." },
      { q: "Les accès administrateurs sont-ils limités aux bonnes personnes ?", weight: 3, inverse: false, rec: "Appliquez le principe du moindre privilège sur les comptes administrateurs." },
      { q: "Les anciens comptes employés sont-ils supprimés rapidement ?", weight: 2, inverse: false, rec: "Automatisez la désactivation immédiate des comptes lors des départs." },
      { q: "Utilisez-vous une solution antivirus / EDR professionnelle ?", weight: 3, inverse: false, rec: "Remplacez les antivirus traditionnels par un EDR managé (SOC)." },
      { q: "Les droits d’accès sont-ils revus régulièrement ?", weight: 2, inverse: false, rec: "Menez des revues périodiques des droits d'accès pour retirer les privilèges obsolètes." },
      { q: "Votre domaine, vos emails et vos informations publiques sont-ils surveillés ?", weight: 2, inverse: false, rec: "Surveillez en continu votre exposition externe et vos fuites d'informations." },
      { q: "Avez-vous mis en place SPF, DKIM et DMARC ?", weight: 2, inverse: false, rec: "Durcissez vos enregistrements DNS pour interdire l'usurpation de votre nom de domaine." },
      { q: "Votre entreprise sait-elle quoi faire en cas de ransomware ?", weight: 3, inverse: false, rec: "Préparez un plan ransomware avec procédures d'isolement et de restauration." },
      { q: "Stockez-vous des données clients sensibles ?", weight: 1, inverse: false, rec: "Renforcez le chiffrement, la segmentation et le contrôle d'accès autour des données clients." }
    ]
  };

  const currentQuestions = questionsData[auditType] || [];

  const handleStart = (type) => {
    setAuditType(type);
    setStep('questions');
    setCurrentIndex(0);
    setAnswers([]);
  };

  const handleAnswer = (choice) => {
    const newAnswers = [...answers, choice];
    setAnswers(newAnswers);

    if (currentIndex < currentQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep('results');
    }
  };

  const handleBackStep = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setAnswers(answers.slice(0, -1));
    } else {
      setStep('choice');
    }
  };

  const auditAnalysis = useMemo(() => {
    if (step !== 'results') return null;

    let maxPossibleScore = 0;
    let userScore = 0;
    const dynamicRecommendations = [];

    answers.forEach((ans, idx) => {
      const item = currentQuestions[idx];
      maxPossibleScore += item.weight;

      let isSecure = false;
      if (!item.inverse && ans === 'oui') isSecure = true;
      if (item.inverse && ans === 'non') isSecure = true;

      if (isSecure) {
        userScore += item.weight;
      } else {
        if (item.rec) dynamicRecommendations.push(item.rec);
      }
    });

    const rawScore = (userScore / maxPossibleScore) * 10;
    const scoreSur10 = Math.max(1, Math.round(rawScore)); 
    
    let risk, color, label, icon;
    
    if (scoreSur10 <= 3) {
      risk = "Élevé"; label = "Sécurité faible"; color = "#ef4444"; icon = <ShieldAlert size={36} />;
    } else if (scoreSur10 >= 4 && scoreSur10 <= 6) {
      risk = "Moyen"; label = "Sécurité moyenne"; color = "#f59e0b"; icon = <AlertTriangle size={36} />;
    } else { 
      risk = "Faible"; label = "Bonne sécurité"; color = "#10b981"; icon = <CheckCircle size={36} />;
    }

    return { scoreSur10, risk, label, color, icon, recommendations: dynamicRecommendations.slice(0, 5) };
  }, [step, answers, currentQuestions]);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailInput) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setTimeout(() => {
        setIsEmailModalOpen(false);
        setIsSubmitted(false);
        setEmailInput('');
      }, 1500);
    }, 1200);
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center font-inter bg-[#0A1931] overflow-hidden select-none py-4 sm:py-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      
      <div className="absolute inset-0 z-0">
        <LiquidEther color0="#4a7fa7" color1="#b3cfe5" color2="#0A1931" autoIntensity={0.8} />
        <div className="absolute inset-0 bg-[#0A1931]/75 backdrop-blur-[4px] z-[1]" />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-xl lg:max-w-4xl mx-4 rounded-[2rem] border border-white/10 bg-white/[0.04] backdrop-blur-3xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] flex flex-col justify-between max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-4rem)] overflow-hidden"
      >
        
        {/* BOUTON RETOUR ACCUEIL GLOBAL FIXE */}
        {step !== 'results' && (
          <a 
            href="/"
            className="fixed sm:absolute top-4 left-4 sm:top-6 sm:left-6 z-20 p-2 sm:p-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[#4A7FA7]/40 transition-all text-white/60 hover:text-white flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-widest cursor-pointer"
            title="Retour à l'accueil"
          >
            <Home size={13} />
            <span className="hidden sm:inline">Accueil</span>
          </a>
        )}

        {/* CONTAINER DU CONTENU AVEC INTEGRATION INLINE DU MASQUAGE DE SCROLLBAR */}
        <div className="p-5 sm:p-7 pt-16 sm:pt-20 overflow-y-auto max-h-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          
          {/* LOGO GRID ET HEADER */}
          <div className="flex justify-between items-center mb-4 sm:mb-5 border-b border-white/5 pb-3">
            <div className="flex items-center gap-2 pl-0 sm:pl-2">
              <ShieldCheck className="text-[#4A7FA7]" size={20} />
              <span className="text-lg sm:text-xl font-black text-white tracking-tighter">Nodixs</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[8px] uppercase font-black tracking-widest text-white/30 hidden xs:inline">
                Nodixs
              </span>
              <span className="text-[9px] uppercase font-black tracking-widest text-white/40 bg-white/5 px-2.5 py-1 rounded border border-white/5">
                Analyse Cyber Intel
              </span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            
            {/* ÉTAPE 1 : SELECTION DU PROFIL */}
            {step === 'choice' && (
              <motion.div 
                key="choice"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
              >
                <h1 className="text-xl sm:text-2xl font-black text-white mb-1.5 tracking-tight">Évaluez votre exposition</h1>
                <p className="text-white/50 text-xs sm:text-sm mb-5">Sélectionnez le périmètre d'analyse pour lancer le moteur d'évaluation.</p>
                
                <div className="flex flex-col gap-2.5">
                  <button 
                    onClick={() => handleStart('particulier')}
                    className="group p-3.5 sm:p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/5 hover:border-[#4A7FA7]/40 transition-all text-left flex items-start gap-3.5"
                  >
                    <div className="p-2.5 sm:p-3 rounded-xl bg-white/5 text-[#4A7FA7] group-hover:scale-105 transition-transform">
                      <User size={22} />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white mb-0.5">Espace Particulier</h3>
                      <p className="text-[11px] sm:text-xs text-white/40">Identité numérique, fuites de données et vie privée.</p>
                    </div>
                  </button>

                  <button 
                    onClick={() => handleStart('entreprise')}
                    className="group p-3.5 sm:p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:bg-white/5 hover:border-[#4A7FA7]/40 transition-all text-left flex items-start gap-3.5"
                  >
                    <div className="p-2.5 sm:p-3 rounded-xl bg-white/5 text-[#4A7FA7] group-hover:scale-105 transition-transform">
                      <Building2 size={22} />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-bold text-white mb-0.5">Espace Infrastructure / Entreprise</h3>
                      <p className="text-[11px] sm:text-xs text-white/40">Gouvernance, résilience système, PSSI et terminaux.</p>
                    </div>
                  </button>
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 2 : TUNNEL DE QUESTIONS */}
            {step === 'questions' && (
              <motion.div 
                key={currentIndex}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
              >
                <div className="mb-4">
                  <div className="flex justify-between text-[10px] font-mono font-bold text-white/40 mb-1.5 uppercase tracking-wider">
                    <span>INDEX // {currentIndex + 1} OF {currentQuestions.length}</span>
                    <span>{Math.round(((currentIndex + 1) / currentQuestions.length) * 100)}%</span>
                  </div>
                  <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#4A7FA7] to-[#B3CFE5] transition-all duration-300"
                      style={{ width: `${((currentIndex + 1) / currentQuestions.length) * 100}%` }}
                    />
                  </div>
                </div>

                <h2 className="text-base sm:text-lg md:text-xl font-bold text-white mb-4 leading-snug min-h-[44px] flex items-center">
                  {currentQuestions[currentIndex].q}
                </h2>

                <div className="flex flex-col gap-2">
                  {[
                    { label: 'Oui', val: 'oui' },
                    { label: 'Non', val: 'non' },
                    { label: 'Je ne sais pas', val: 'jsaispas' }
                  ].map((btn) => (
                    <button
                      key={btn.val}
                      onClick={() => handleAnswer(btn.val)}
                      className="group flex items-center justify-between p-3 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-[#4A7FA7] hover:border-[#4A7FA7] transition-all text-left"
                    >
                      <span className="text-white text-xs sm:text-sm font-bold group-hover:translate-x-1.5 transition-transform">
                        {btn.label}
                      </span>
                      <ChevronRight size={15} className="text-white/20 group-hover:text-white transition-colors" />
                    </button>
                  ))}
                </div>

                <div className="mt-5 flex flex-col items-center gap-2 border-t border-white/5 pt-4">
                  <button 
                    onClick={handleBackStep}
                    className="flex items-center gap-1.5 text-white/50 hover:text-white text-[11px] font-bold transition-colors"
                  >
                    <ArrowLeft size={12} /> Question précédente
                  </button>
                  <button 
                    onClick={() => setStep('choice')}
                    className="text-white/25 hover:text-white/50 text-[10px] font-medium transition-colors"
                  >
                    Retourner à la sélection
                  </button>
                </div>
              </motion.div>
            )}

            {/* ÉTAPE 3 : RÉSULTATS */}
            {step === 'results' && auditAnalysis && (
              <motion.div 
                key="results"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-0.5"
              >
                <div className="lg:grid lg:grid-cols-[200px,minmax(0,1fr)] lg:gap-6 lg:items-start">
                  <div className="lg:sticky lg:top-0 flex flex-row lg:flex-col items-center justify-center lg:justify-start gap-4 lg:gap-0 mb-4 lg:mb-0">
                  
                    <div className="relative w-18 h-18 sm:w-22 sm:h-22 flex items-center justify-center">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                        <circle 
                          cx="50" cy="50" r="42" 
                          stroke="rgba(255,255,255,0.03)" 
                          strokeWidth="6" fill="transparent" 
                        />
                        <motion.circle 
                          cx="50" cy="50" r="42" 
                          stroke={auditAnalysis.color} 
                          strokeWidth="6" fill="transparent" 
                          strokeDasharray="263.8"
                          initial={{ strokeDashoffset: 263.8 }}
                          animate={{ strokeDashoffset: 263.8 - (263.8 * auditAnalysis.scoreSur10) / 10 }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          strokeLinecap="round"
                          style={{ filter: `drop-shadow(0px 0px 6px ${auditAnalysis.color}80)` }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-xl sm:text-2xl font-black text-white leading-none">{auditAnalysis.scoreSur10}</span>
                        <span className="text-[8px] text-white/30 font-bold uppercase tracking-widest mt-0.5">Score</span>
                      </div>
                    </div>

                    <div className="text-left lg:text-center lg:mt-3">
                      <p className="text-xs sm:text-sm font-black uppercase tracking-wider" style={{ color: auditAnalysis.color }}>
                        {auditAnalysis.label}
                      </p>
                      <span className="text-white/40 text-[10px] font-medium font-mono block mt-0.5">// RISQUE {auditAnalysis.risk}</span>
                    </div>
                  </div>

                  <div>
                    <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-3 sm:p-4 text-left mb-4">
                      <h4 className="text-white text-[10px] font-black mb-2 flex items-center gap-1.5 uppercase tracking-wider text-white/60">
                        <ShieldCheck size={14} className="text-[#4A7FA7]" /> Recommandations prioritaires
                      </h4>
                      {auditAnalysis.recommendations.length > 0 ? (
                        <ul className="space-y-1.5">
                          {auditAnalysis.recommendations.map((rec, index) => (
                            <li key={index} className="text-white/70 text-[11px] sm:text-xs leading-relaxed flex items-start gap-2">
                              <span className="text-[9px] bg-white/5 px-1.5 py-0.2 rounded font-mono text-white/40 mt-0.5">{index+1}</span>
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-white/50 text-xs leading-relaxed">Aucune vulnérabilité critique détectée sur la base de vos réponses.</p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button 
                          onClick={() => window.location.href = '/rdv'}
                          className="flex-1 bg-[#4A7FA7] hover:bg-[#1A3D63] text-white py-2.5 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 transition-all shadow-lg shadow-[#4A7FA7]/10"
                        >
                          <Calendar size={13} /> Demander un audit
                        </button>
                        <button 
                          onClick={() => setIsEmailModalOpen(true)}
                          className="flex-1 bg-white/5 border border-white/10 hover:bg-white/10 text-white py-2.5 rounded-xl font-black text-xs flex items-center justify-center gap-1.5 transition-all"
                        >
                          <Download size={13} /> Télécharger le rapport PDF
                        </button>
                      </div>

                      <a 
                        href="/"
                        className="w-full bg-white/10 border border-white/20 hover:bg-white/20 hover:border-white/30 text-white py-3 rounded-xl font-black text-xs flex items-center justify-center gap-2 transition-all mt-1"
                      >
                        <Home size={14} /> Retourner à l'accueil
                      </a>
                    </div>

                    <button 
                      onClick={() => setStep('choice')}
                      className="mt-5 text-white/30 hover:text-white text-[9px] font-black uppercase tracking-widest transition-colors block mx-auto"
                    >
                      Réinitialiser l'analyse
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </motion.div>

      {/* LEAD MODAL */}
      <AnimatePresence>
        {isEmailModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsEmailModalOpen(false)}
              className="absolute inset-0 bg-[#0A1931]/80 backdrop-blur-md"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              className="relative w-full max-w-md bg-white/[0.04] border border-white/10 rounded-2xl p-6 shadow-2xl backdrop-blur-2xl z-10"
            >
              <button 
                onClick={() => setIsEmailModalOpen(false)}
                className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="text-center">
                <div className="w-12 h-12 bg-[#4A7FA7]/10 text-[#4A7FA7] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail size={22} />
                </div>
                <h3 className="text-lg font-black text-white mb-1.5">Recevoir votre rapport complet</h3>
                <p className="text-white/50 text-xs mb-5">
                  Entrez votre adresse email professionnelle pour recevoir la cartographie complète de vos vulnérabilités au format PDF.
                </p>

                <AnimatePresence mode="wait">
                  {!isSubmitted ? (
                    <motion.form 
                      key="form"
                      onSubmit={handleEmailSubmit} 
                      className="flex flex-col gap-2.5"
                    >
                      <input 
                        type="email" 
                        required
                        placeholder="exemple@entreprise.fr"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder-white/20 focus:outline-none focus:border-[#4A7FA7] transition-colors"
                      />
                      <button 
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#4A7FA7] hover:bg-[#1A3D63] text-white py-3 rounded-xl font-black text-xs flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Génération du rapport...
                          </>
                        ) : (
                          "Envoyer le rapport par email"
                        )}
                      </button>
                    </motion.form>
                  ) : (
                    <motion.div 
                      key="success"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-[#10b981] bg-[#10b981]/10 border border-[#10b981]/20 p-4 rounded-xl text-xs font-bold"
                    >
                      Rapport envoyé avec succès. Vérifiez votre boîte de réception.
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}